import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { useToggleIndex } from 'packages/common-frontend/hooks/useToggleIndex';
import { dollarNumberWithCommasOrDash } from 'packages/common-frontend/utils';
import {
  ExpensesFeesTransactions,
  LedgerTransactionWithAccountNameAndType,
} from 'packages/statements/ui/expensesFeesUtils';

const ExpensesFeesHeader = () => (
  <Thead>
    <Tr>
      <Th width="25%">Date</Th>
      <Th width="20%" textAlign="center">
        Debit
      </Th>
      <Th width="20%" textAlign="center">
        Credit
      </Th>
      <Th width="25%" textAlign="center">
        Total balance
      </Th>
      <Th width="10%" />
    </Tr>
  </Thead>
);

type ExpensesFeesRowProps = {
  transactionsByDay: ExpensesFeesTransactions;
};

type ExpensesFeesTdProps = {
  leftAlign?: boolean;
  hideBorder?: boolean;
  subCell?: boolean;
};

const ExpensesFeesTd: React.FC<ExpensesFeesTdProps> = ({
  leftAlign,
  hideBorder,
  subCell,
  children,
}) => (
  <Td
    textAlign={leftAlign ? 'left' : 'center'}
    border={hideBorder ? 'none' : undefined}
    paddingInlineStart={subCell ? '50px' : undefined}
  >
    {children}
  </Td>
);

type ExpensesFeesDetailsRowProps = {
  transactionsForDay: LedgerTransactionWithAccountNameAndType[];
};

const ExpensesFeesDetailsTable: React.FC<ExpensesFeesDetailsRowProps> = ({
  transactionsForDay,
}) => {
  return (
    <>
      {_.map(
        transactionsForDay,
        (
          { postingDate, organizationName, accountName, type, amount, balance },
          index,
        ) => {
          const isLastTransaction = index === transactionsForDay.length - 1;

          return (
            <Tr
              key={`${postingDate}-${organizationName}-${accountName}-${amount}-${balance}`}
              style={{ cursor: 'default' }}
            >
              <ExpensesFeesTd hideBorder={!isLastTransaction} leftAlign subCell>
                {`${organizationName} - ${accountName}`}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={!isLastTransaction}>
                {type === 'debit' ? dollarNumberWithCommasOrDash(amount) : ''}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={!isLastTransaction}>
                {type === 'credit' ? dollarNumberWithCommasOrDash(amount) : ''}
              </ExpensesFeesTd>
            </Tr>
          );
        },
      )}
    </>
  );
};

const ExpensesFeesTableBody: React.FC<ExpensesFeesRowProps> = ({
  transactionsByDay,
}) => {
  const { activeIndex, toggleIndex } = useToggleIndex();

  return (
    <Tbody>
      {_.map(transactionsByDay, (transactionsForDay, index) => {
        const isActiveIndex = activeIndex === index;
        const { postingDate } = transactionsForDay[0];
        const totalDebit = _.sumBy(transactionsForDay, (transactionForDay) => {
          if (transactionForDay.type === 'debit') {
            return transactionForDay.amount;
          }
          return 0;
        });
        const totalCredit = _.sumBy(transactionsForDay, (transactionForDay) => {
          if (transactionForDay.type === 'credit') {
            return transactionForDay.amount;
          }
          return 0;
        });

        return (
          <React.Fragment key={postingDate}>
            <Tr
              onClick={() => transactionsForDay.length && toggleIndex(index)}
              style={{ cursor: 'pointer' }}
            >
              <ExpensesFeesTd leftAlign hideBorder={isActiveIndex}>
                {commonDate.fullDateStrToHumanDateString(postingDate)}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={isActiveIndex}>
                {totalDebit ? dollarNumberWithCommasOrDash(totalDebit) : ''}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={isActiveIndex}>
                {totalCredit ? dollarNumberWithCommasOrDash(totalCredit) : ''}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={isActiveIndex}>
                {dollarNumberWithCommasOrDash(transactionsForDay[0].balance)}
              </ExpensesFeesTd>
              <ExpensesFeesTd hideBorder={isActiveIndex}>
                {isActiveIndex ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </ExpensesFeesTd>
            </Tr>
            {isActiveIndex && (
              <ExpensesFeesDetailsTable
                transactionsForDay={transactionsForDay}
              />
            )}
          </React.Fragment>
        );
      })}
    </Tbody>
  );
};

export const ExpensesFeesTable = ({
  transactionsByDay,
}: {
  transactionsByDay: ExpensesFeesTransactions;
}) => (
  <Table>
    <ExpensesFeesHeader />
    <ExpensesFeesTableBody transactionsByDay={transactionsByDay} />
  </Table>
);
